/**
 * Riflessioni - letture pubbliche della sezione /riflessioni (il "blog" del sito).
 *
 * Le scritture passano dal gestionale: vedi adminApiService.ts e useAdminApi.ts.
 */

import { supabase } from '@/lib/supabase';

export interface Riflessione {
  id: number;
  slug: string;
  title: string;
  excerpt: string | null;
  /** HTML prodotto dall'editor del gestionale */
  content: string;
  cover_image: string | null;
  is_published: boolean;
  /** Data civile `YYYY-MM-DD`: formattarla con formatBookingDate, mai con new Date(stringa) */
  published_at: string;
  created_at: string | null;
  updated_at: string | null;
}

/** Quanto serve per una card: il testo completo si carica solo aprendo l'articolo */
export type RiflessioneSummary = Pick<
  Riflessione,
  'id' | 'slug' | 'title' | 'excerpt' | 'cover_image' | 'published_at'
>;

const SUMMARY_COLUMNS = 'id, slug, title, excerpt, cover_image, published_at';

/**
 * Riflessioni pubblicate, dalla più recente.
 * Se la tabella non è raggiungibile restituisce un elenco vuoto: la sezione è un
 * contenuto in più e non deve mai rompere la homepage o il menu.
 */
export async function getPublishedRiflessioni(limit?: number): Promise<RiflessioneSummary[]> {
  let query = supabase
    .from('riflessioni')
    .select(SUMMARY_COLUMNS)
    .eq('is_published', true)
    .order('published_at', { ascending: false })
    .order('id', { ascending: false });

  if (limit) query = query.limit(limit);

  const { data, error } = await query;
  if (error) {
    console.warn('Riflessioni non disponibili:', error.message);
    return [];
  }
  return data || [];
}

/**
 * Una riflessione pubblicata dal suo slug.
 * Le bozze qui non arrivano mai (RLS): l'anteprima dell'admin passa dal server,
 * vedi getRiflessioneForPreview in adminApiService.ts.
 */
export async function getRiflessioneBySlug(slug: string): Promise<Riflessione | null> {
  const { data, error } = await supabase
    .from('riflessioni')
    .select('*')
    .eq('slug', slug)
    .eq('is_published', true)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/** Minuti di lettura stimati dal testo HTML (circa 200 parole al minuto) */
export function readingMinutes(html: string): number {
  const words = html.replace(/<[^>]+>/g, ' ').trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}
